/**
 * useShellSheetEdits - hand edits to a combatant's sheet from either panel.
 *
 * `updateTab` works for ANY combatant id, so the actor panel edits its own
 * defensive profile with the very same controls the target panel uses
 * (v7.1.0): the defender custom modifiers are CURRIED on a combatant id, and
 * the armour prompt takes one. On a vehicle the prompt edits the active
 * FACING's armour, not the hit location.
 *
 * Returns the target-side and actor-side handlers. Extracted from
 * DeathWatchSimulator.
 */
import { useMemo, useCallback } from 'react';
import { getBodyConfig } from '../engine/bodyConfig';
import { effectiveVehicleFacing } from '../engine/vehicleFacing';
import { cleanedAfterTraitEdit } from '../engine/traitCleanup';
import { computeTraitCleanup } from '../engine/conditionEligibility';
import { bodyTypeOf } from './useShellActionDrum';
import useStableCallback from './useStableCallback';

/** A target with its defender custom modifiers replaced by `edit(mods)`. */
const withMods = (t, edit) => ({
  ...t,
  target: { ...t.target, defenderCustomMods: edit(t.target.defenderCustomMods || []) },
});

/** Add, remove and edit handlers for one combatant's defender modifiers. */
export function defenderModHandlers(updateTab, combatantId) {
  return {
    add: (mod) => updateTab(combatantId, (t) => withMods(t, (mods) => [...mods, mod])),
    remove: (modId) => updateTab(combatantId, (t) => withMods(t, (mods) => mods.filter((m) => m.id !== modId))),
    edit: (modId, updates) =>
      updateTab(combatantId, (t) =>
        withMods(t, (mods) => mods.map((m) => (m.id === modId ? { ...m, ...updates } : m))),
      ),
  };
}

/** Which armour a click edits, and what the prompt calls it: a vehicle's facing, else the location. */
export function armourPrompt(target, locId) {
  if (getBodyConfig(bodyTypeOf(target)).armourMode === 'facing') {
    const facing = effectiveVehicleFacing(target);
    return { key: facing, label: facing.charAt(0).toUpperCase() + facing.slice(1) + ' Facing' };
  }
  return { key: locId, label: locId.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()) };
}

/** Ask for an armour value and write it. */
async function editArmour(locId, combatantId, { tabs, showDialogAsync, updateTab }) {
  const tab = tabs.find((t) => t.id === combatantId);
  if (!tab) return;
  const { key, label } = armourPrompt(tab.target, locId);
  const val = await showDialogAsync('prompt', 'Set ' + label + ' armour:', String(tab.target.armour[key] || 0));
  if (val === null) return;
  updateTab(combatantId, (t) => ({
    ...t,
    // A count: never below zero, whatever was typed (v8.391.1, found by ./dev fuzz).
    target: { ...t.target, armour: { ...t.target.armour, [key]: Math.max(0, parseInt(val) || 0) } },
  }));
}

/** The standing reaction (v8.325.0): absent for Ask me, never a literal undefined. */
function withStandingReaction(t, value) {
  const next = { ...t.target };
  if (value) next.standingReaction = value;
  else delete next.standingReaction;
  return { ...t, target: next, label: next.name };
}

/** The actor's own sheet edits, each a no-op with nobody acting. */
function useActorEdits(actorId, actorTarget, updateTab, handleEditArmour) {
  const edit = useCallback(
    (updater) => {
      if (actorId) updateTab(actorId, updater);
    },
    [actorId, updateTab],
  );
  return {
    // Frenzy going on strips what it is immune to (Stunned, Fatigued), as the target panel's does.
    onActorToggleFrenzied: useCallback(
      () =>
        edit((t) => {
          const frenzied = !actorTarget?.frenzied;
          const conditions = frenzied
            ? computeTraitCleanup(t.target.conditions, 'frenzied', true).cleaned
            : t.target.conditions;
          return { ...t, target: { ...t.target, frenzied, conditions } };
        }),
      [edit, actorTarget],
    ),
    onActorSetStandingReaction: useCallback((value) => edit((t) => withStandingReaction(t, value)), [edit]),
    // A trait switched on strips what it is immune to, as on the target panel.
    onActorTargetChange: useCallback(
      (next) => edit((t) => ({ ...t, target: cleanedAfterTraitEdit(t.target, next).target, label: next.name })),
      [edit],
    ),
    // Stable, as handleEditArmour is: the actor's body plate is memoised and only calls it on a right-click.
    onActorEditArmour: useStableCallback((locId) => {
      if (actorId) handleEditArmour(locId, actorId);
    }),
    onActorReorderWeapons: useCallback(
      (weapons) => edit((t) => ({ ...t, target: { ...t.target, defenderWeapons: weapons } })),
      [edit],
    ),
  };
}

/**
 * @param {object} args - tabs, activeTab, actorId, actorTarget (the actor's
 *   projection), updateTab, showDialogAsync.
 */
export default function useShellSheetEdits({ tabs, activeTab, actorId, actorTarget, updateTab, showDialogAsync }) {
  const targetModHandlers = useMemo(() => defenderModHandlers(updateTab, activeTab), [updateTab, activeTab]);
  const actorModHandlers = useMemo(() => defenderModHandlers(updateTab, actorId), [updateTab, actorId]);
  // STABLE (2026-10-30): it read `tabs`, new on every roster change, and both body plates re-rendered with it.
  const handleEditArmour = useStableCallback((locId, combatantId = activeTab) =>
    editArmour(locId, combatantId, { tabs, showDialogAsync, updateTab }),
  );
  return {
    targetModHandlers,
    actorModHandlers,
    handleEditArmour,
    ...useActorEdits(actorId, actorTarget, updateTab, handleEditArmour),
  };
}
